import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'sonner';

import App from '@/App';
import { AuthProvider } from '@/auth/auth-context';
import i18n from '@/i18n/i18n';
import { TOASTER_CONFIG } from '@/lib/toast';
import { initTheme } from '@/lib/theme';
import '@/index.css';
// SP1 design language — self-hosted Inter (variable). No Google Fonts request;
// one variable file covers all weights (replaces DM Sans).
import '@fontsource-variable/inter';

// Apply stored theme before first render to avoid flash of wrong colors.
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

// SP3c follow-up (in-app unsaved-changes guard): a DATA router — one splat
// route wrapping the existing `<Routes>` tree in router.tsx — replaces the
// plain <BrowserRouter>. `useBlocker` (composer editor's navigation guard)
// only works under a data router; route matching itself is unchanged.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <App />
        <Toaster {...TOASTER_CONFIG} />
      </AuthProvider>
    ),
  },
]);

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </I18nextProvider>
  </React.StrictMode>,
);
